import React, { useState } from 'react';
import styled from 'styled-components';
import { Phone, Shield, User, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const Logo = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const Subtitle = styled.p`
  color: #666;
  margin-bottom: 30px;
  font-size: 1.1rem;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
  text-align: right;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px;
  border: 2px solid #e1e5e9;
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.3s ease;
  text-align: right;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  width: 100%;
  padding: 15px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 10px;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
    }
  ` : `
    background: #f8f9fa;
    color: #333;
    border: 2px solid #e1e5e9;
    
    &:hover {
      background: #e9ecef;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Message = styled.div<{ type: 'success' | 'error' }>`
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-weight: 500;
  
  ${props => props.type === 'success' ? `
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  ` : `
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `}
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
`;

const Step = styled.div<{ active: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin: 0 5px;
  background: ${props => props.active ? '#667eea' : '#e1e5e9'};
  transition: all 0.3s ease;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #667eea;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 20px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const LoginForm: React.FC = () => {
  const { login, verifyCode } = useAuth();
  const [step, setStep] = useState<'phone' | 'verify'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) return;

    setIsLoading(true);
    setMessage(null);

    try {
      const result = await login(phone);
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        setStep('verify');
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'שגיאה בהתחברות' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setIsLoading(true);
    setMessage(null);

    try {
      const result = await verifyCode(phone, code);
      if (result.success) {
        // User will be redirected by the auth context
        window.location.reload();
      } else {
        setMessage({ type: 'error', text: 'קוד לא נכון או פג תוקף' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'שגיאה באימות הקוד' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep('phone');
    setCode('');
    setMessage(null);
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Logo>
          <Shield size={32} />
          זכרון דליה
        </Logo>
        <Subtitle>מערכת ניהול בית כנסת</Subtitle>

        <StepIndicator>
          <Step active={step === 'phone'} />
          <Step active={step === 'verify'} />
        </StepIndicator>

        {message && (
          <Message type={message.type}>
            {message.text}
          </Message>
        )}

        {step === 'phone' ? (
          <form onSubmit={handlePhoneSubmit}>
            <InputGroup>
              <Label>מספר טלפון</Label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="050-1234567"
                dir="ltr"
                required
              />
            </InputGroup>

            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'שולח...' : 'שלח קוד אימות'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleCodeSubmit}>
            <BackButton type="button" onClick={handleBack}>
              <ArrowLeft size={16} />
              חזור
            </BackButton>

            <InputGroup>
              <Label>קוד האימות</Label>
              <Input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                dir="ltr"
                maxLength={6}
                required
              />
            </InputGroup>

            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'מאמת...' : 'אמת קוד'}
            </Button>
          </form>
        )}
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginForm;
